/**
 * AP-11-E (E5-C) — Abschnitt „Eigener Speicher" auf der Seite „Speicher".
 *
 * Kein Feld speichert hier von selbst: Die Zugangsdaten zu fremder Infrastruktur
 * sind eine **Handlung** (AP-36, Fall 3 — „Zugangsdaten ändern"), und Verbinden
 * zieht alle Dateien des Betriebs um. Deshalb zwei Knöpfe mit ihrem Verb:
 * „Verbindung testen" ändert nichts, „Verbinden und umziehen" fragt vorher nach.
 */
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Check, Cloud, Loader2 } from 'lucide-react'
import { cardTitle, fieldLabel, fieldWrapper, formCard, inputBase } from '@/lib/form-styles'
import { bestaetige } from '@/lib/dialoge'
import { fehlertext } from '@/lib/fehlertext'
import { fmtDateLong } from '@/lib/format-date'
import {
  eigenerSpeicherApi,
  speicherText,
  umzugProzent,
  type EigenerSpeicher as Stand,
  type EigenerSpeicherEingabe,
} from '@/lib/speicher'

type Anbieter = 'AWS' | 'ANDERER'

const LEER = { endpoint: '', bucket: '', region: '', accessKey: '', secretKey: '' }

/** Solange ein Umzug läuft, wird der Stand so oft neu geholt. */
const NACHFRAGEN_MS = 2000

export function EigenerSpeicher({ onGeaendert }: { onGeaendert: () => void }) {
  const { t } = useTranslation()
  const [stand, setStand] = useState<Stand | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  const lade = useCallback(() => {
    eigenerSpeicherApi
      .stand()
      .then((s) => {
        setStand(s)
        setFehler(null)
      })
      .catch((e) => setFehler(fehlertext(e, t)))
  }, [t])

  useEffect(lade, [lade])

  // Der Umzug läuft im Hintergrund weiter — die Seite fragt nach, bis er fertig ist.
  const laeuft = stand?.move?.status === 'RUNNING'
  useEffect(() => {
    if (!laeuft) return
    const id = window.setInterval(lade, NACHFRAGEN_MS)
    return () => window.clearInterval(id)
  }, [laeuft, lade])

  // Mit dem Ende des Umzugs ändert sich auch die Belegung darüber.
  const umzugStatus = stand?.move?.status
  useEffect(() => {
    if (umzugStatus === 'DONE') onGeaendert()
  }, [umzugStatus, onGeaendert])

  const uebernimm = (s: Stand) => {
    setStand(s)
    onGeaendert()
  }

  return (
    <section className={formCard} aria-labelledby="eigener-speicher">
      <h2 id="eigener-speicher" className={`${cardTitle} flex items-center gap-2`}>
        <Cloud size={18} /> {t('storage.eigen.titel')}
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">{t('storage.eigen.untertitel')}</p>

      {fehler && <p className="mt-3 text-sm text-destructive">{fehler}</p>}
      {stand && !stand.available && <p className="mt-3 text-sm text-muted-foreground">{t('storage.eigen.nichtEingerichtet')}</p>}
      {stand && stand.available && (
        <>
          <Verbindung stand={stand} />
          {stand.move && stand.move.status !== 'DONE' && <Umzug stand={stand} onStand={uebernimm} />}
          {stand.move?.status === 'DONE' && stand.move.finishedAt && (
            <p className="mt-3 flex items-center gap-1.5 text-sm text-emerald-700 dark:text-emerald-400">
              <Check size={14} />
              {t('storage.eigen.umzug.fertig', {
                anzahl: new Intl.NumberFormat('de-DE').format(stand.move.movedObjects),
                groesse: speicherText(stand.move.movedBytes),
                datum: fmtDateLong(stand.move.finishedAt),
              })}
            </p>
          )}
          <Formular stand={stand} onStand={uebernimm} />
          {stand.provider === 's3' && <Rueckweg stand={stand} onStand={uebernimm} />}
        </>
      )}
    </section>
  )
}

/** Wo die Dateien liegen — und welcher Schlüssel hinterlegt ist. */
function Verbindung({ stand }: { stand: Stand }) {
  const { t } = useTranslation()
  if (stand.provider === 'central') {
    return <p className="mt-3 text-sm text-foreground">{t('storage.eigen.liegtBeiSymphenia')}</p>
  }
  return (
    <>
      <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-sm">
        <dt className="text-muted-foreground">{t('storage.eigen.feld.bucket')}</dt>
        <dd className="font-medium text-foreground">{stand.bucket}</dd>
        <dt className="text-muted-foreground">{t('storage.eigen.speicher')}</dt>
        <dd className="text-foreground">
          {stand.endpoint ?? t('storage.eigen.amazonRegion', { region: stand.region ?? '' })}
        </dd>
        <dt className="text-muted-foreground">{t('storage.eigen.feld.accessKey')}</dt>
        <dd className="text-foreground">…{stand.accessKeySuffix}</dd>
        {stand.connectedAt && (
          <>
            <dt className="text-muted-foreground">{t('storage.eigen.verbundenSeit')}</dt>
            <dd className="text-foreground">{fmtDateLong(stand.connectedAt)}</dd>
          </>
        )}
      </dl>
      {stand.lastFailureAt && (
        <div className="mt-3 flex items-start gap-3 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-foreground">
          <AlertTriangle size={18} className="mt-0.5 shrink-0 text-destructive" />
          <div>
            <p className="font-medium">{t('storage.eigen.stoerung.titel', { datum: fmtDateLong(stand.lastFailureAt) })}</p>
            <p className="mt-1 text-muted-foreground">
              {stand.lastFailureMessage} {t('storage.eigen.stoerung.folge')}
            </p>
          </div>
        </div>
      )}
    </>
  )
}

/** Der laufende oder gescheiterte Umzug. */
function Umzug({ stand, onStand }: { stand: Stand; onStand: (s: Stand) => void }) {
  const { t } = useTranslation()
  const [fehler, setFehler] = useState<string | null>(null)
  const [beschaeftigt, setBeschaeftigt] = useState(false)
  const u = stand.move!
  const prozent = umzugProzent(u)
  const gescheitert = u.status === 'FAILED'

  const erneut = async () => {
    setBeschaeftigt(true)
    setFehler(null)
    try {
      onStand(await eigenerSpeicherApi.fortsetzen())
    } catch (e) {
      setFehler(fehlertext(e, t))
    } finally {
      setBeschaeftigt(false)
    }
  }

  return (
    <div className="mt-4 rounded-md border border-border bg-background px-4 py-3">
      <p className="flex items-center gap-2 text-sm font-medium text-foreground">
        {!gescheitert && <Loader2 size={14} className="animate-spin" />}
        {t(`storage.eigen.umzug.richtung.${u.direction}`)}
      </p>
      <div
        className="mt-2 h-2 overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={prozent}
        aria-label={t('storage.eigen.umzug.fortschritt')}
      >
        <div className={`h-full ${gescheitert ? 'bg-destructive' : 'bg-primary'}`} style={{ width: `${prozent}%` }} />
      </div>
      <p className="mt-2 text-sm text-muted-foreground">
        {t('storage.eigen.umzug.stand', {
          bewegt: new Intl.NumberFormat('de-DE').format(u.movedObjects),
          gesamt: new Intl.NumberFormat('de-DE').format(u.totalObjects),
          groesse: speicherText(u.movedBytes),
        })}{' '}
        {!gescheitert && t('storage.eigen.umzug.laeuftWeiter')}
      </p>
      {gescheitert && (
        <div className="mt-2 text-sm">
          <p className="text-destructive">
            {t('storage.eigen.umzug.gescheitert')} {u.lastError}
          </p>
          <p className="mt-1 text-muted-foreground">{t('storage.eigen.umzug.nichtsVerloren')}</p>
          <button
            type="button"
            onClick={() => void erneut()}
            disabled={beschaeftigt}
            className="mt-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {t('storage.eigen.umzug.erneut')}
          </button>
        </div>
      )}
      {fehler && <p className="mt-2 text-sm text-destructive">{fehler}</p>}
    </div>
  )
}

/** Verbindung eintragen, testen, verbinden. */
function Formular({ stand, onStand }: { stand: Stand; onStand: (s: Stand) => void }) {
  const { t } = useTranslation()
  const verbunden = stand.provider === 's3'
  const [offen, setOffen] = useState(false)
  const [anbieter, setAnbieter] = useState<Anbieter>('AWS')
  const [werte, setWerte] = useState(LEER)
  const [beschaeftigt, setBeschaeftigt] = useState<'test' | 'verbinden' | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [testOk, setTestOk] = useState(false)
  const umzugOffen = !!stand.move && stand.move.status !== 'DONE'

  const setze = (feld: keyof typeof LEER) => (e: { target: { value: string } }) => {
    setWerte((w) => ({ ...w, [feld]: e.target.value }))
    setTestOk(false)
    setFehler(null)
  }

  const eingabe = (): EigenerSpeicherEingabe => ({
    endpoint: anbieter === 'AWS' ? null : werte.endpoint.trim(),
    bucket: werte.bucket.trim(),
    region: werte.region.trim() || null,
    accessKey: werte.accessKey.trim(),
    secretKey: werte.secretKey.trim(),
  })

  const vollstaendig =
    werte.bucket.trim() !== '' &&
    werte.accessKey.trim() !== '' &&
    werte.secretKey.trim() !== '' &&
    (anbieter === 'AWS' ? werte.region.trim() !== '' : werte.endpoint.trim() !== '')

  const teste = async () => {
    setBeschaeftigt('test')
    setFehler(null)
    setTestOk(false)
    try {
      await eigenerSpeicherApi.teste(eingabe())
      setTestOk(true)
    } catch (e) {
      setFehler(fehlertext(e, t))
    } finally {
      setBeschaeftigt(null)
    }
  }

  const verbinde = async () => {
    const derselbe = verbunden && stand.bucket === werte.bucket.trim() && (stand.endpoint ?? '') === (anbieter === 'AWS' ? '' : werte.endpoint.trim().replace(/\/+$/, ''))
    const frage = derselbe ? t('storage.eigen.frage.schluessel') : t(verbunden ? 'storage.eigen.frage.wechsel' : 'storage.eigen.frage.verbinden')
    if (!(await bestaetige(frage, { ok: t(derselbe ? 'storage.eigen.knopf.schluesselTauschen' : 'storage.eigen.knopf.verbinden') }))) return

    setBeschaeftigt('verbinden')
    setFehler(null)
    try {
      onStand(await eigenerSpeicherApi.verbinde(eingabe()))
      // Die Schlüssel bleiben nicht im Formular stehen.
      setWerte(LEER)
      setTestOk(false)
      setOffen(false)
    } catch (e) {
      setFehler(fehlertext(e, t))
    } finally {
      setBeschaeftigt(null)
    }
  }

  if (umzugOffen) return null

  if (verbunden && !offen) {
    return (
      <button
        type="button"
        onClick={() => setOffen(true)}
        className="mt-4 rounded-md border border-input bg-background px-3 py-1.5 text-sm font-medium text-foreground hover:bg-accent"
      >
        {t('storage.eigen.knopf.aendern')}
      </button>
    )
  }

  return (
    <form
      className="mt-4 space-y-3 border-t border-border pt-4"
      onSubmit={(e) => {
        e.preventDefault()
        if (vollstaendig && !beschaeftigt) void verbinde()
      }}
    >
      {!verbunden && <p className="text-sm text-muted-foreground">{t('storage.eigen.einleitung')}</p>}
      {verbunden && <p className="text-sm text-muted-foreground">{t('storage.eigen.einleitungWechsel')}</p>}

      <fieldset className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-foreground">
        <legend className="sr-only">{t('storage.eigen.speicher')}</legend>
        {(['AWS', 'ANDERER'] as const).map((a) => (
          <label key={a} className="flex items-center gap-2">
            <input
              type="radio"
              name="speicher-anbieter"
              checked={anbieter === a}
              onChange={() => {
                setAnbieter(a)
                setTestOk(false)
              }}
            />
            {t(`storage.eigen.anbieter.${a}`)}
          </label>
        ))}
      </fieldset>

      {anbieter === 'ANDERER' && (
        <label className={`${fieldWrapper} block`}>
          <span className={fieldLabel}>{t('storage.eigen.feld.endpoint')}</span>
          <input className={inputBase} value={werte.endpoint} onChange={setze('endpoint')} placeholder="https://s3.example.com" autoComplete="off" />
          <span className="block text-xs text-muted-foreground">{t('storage.eigen.hilfe.endpoint')}</span>
        </label>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <label className={`${fieldWrapper} block`}>
          <span className={fieldLabel}>{t('storage.eigen.feld.bucket')}</span>
          <input className={inputBase} value={werte.bucket} onChange={setze('bucket')} autoComplete="off" />
        </label>
        <label className={`${fieldWrapper} block`}>
          <span className={fieldLabel}>
            {t('storage.eigen.feld.region')}
            {anbieter === 'ANDERER' && <span className="ml-1 font-normal normal-case">{t('storage.eigen.optional')}</span>}
          </span>
          <input className={inputBase} value={werte.region} onChange={setze('region')} placeholder="eu-central-1" autoComplete="off" />
        </label>
        <label className={`${fieldWrapper} block`}>
          <span className={fieldLabel}>{t('storage.eigen.feld.accessKey')}</span>
          <input className={inputBase} value={werte.accessKey} onChange={setze('accessKey')} autoComplete="off" />
        </label>
        <label className={`${fieldWrapper} block`}>
          <span className={fieldLabel}>{t('storage.eigen.feld.secretKey')}</span>
          <input className={inputBase} type="password" value={werte.secretKey} onChange={setze('secretKey')} autoComplete="new-password" />
        </label>
      </div>
      <p className="text-xs text-muted-foreground">{t('storage.eigen.hilfe.rechte')}</p>

      {fehler && <p className="text-sm text-destructive">{fehler}</p>}
      {testOk && (
        <p className="flex items-center gap-1.5 text-sm text-emerald-700 dark:text-emerald-400">
          <Check size={14} /> {t('storage.eigen.testOk')}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => void teste()}
          disabled={!vollstaendig || beschaeftigt !== null}
          className="rounded-md border border-input bg-background px-3 py-1.5 text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50"
        >
          {beschaeftigt === 'test' ? t('storage.eigen.knopf.testet') : t('storage.eigen.knopf.testen')}
        </button>
        <button
          type="submit"
          disabled={!vollstaendig || beschaeftigt !== null}
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {beschaeftigt === 'verbinden' ? t('storage.eigen.knopf.verbindet') : t('storage.eigen.knopf.verbinden')}
        </button>
        {verbunden && (
          <button
            type="button"
            onClick={() => {
              setOffen(false)
              setWerte(LEER)
              setFehler(null)
              setTestOk(false)
            }}
            className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            {t('common.cancel')}
          </button>
        )}
      </div>
    </form>
  )
}

/** Zurück auf den Symphenia-Speicher — nur mit Zustimmung von Symphenia. */
function Rueckweg({ stand, onStand }: { stand: Stand; onStand: (s: Stand) => void }) {
  const { t } = useTranslation()
  const [fehler, setFehler] = useState<string | null>(null)
  const [beschaeftigt, setBeschaeftigt] = useState(false)
  if (stand.move && stand.move.status !== 'DONE') return null

  const zurueck = async () => {
    if (!(await bestaetige(t('storage.eigen.frage.zurueck'), { ok: t('storage.eigen.knopf.zurueck') }))) return
    setBeschaeftigt(true)
    setFehler(null)
    try {
      onStand(await eigenerSpeicherApi.zurueck())
    } catch (e) {
      setFehler(fehlertext(e, t))
    } finally {
      setBeschaeftigt(false)
    }
  }

  return (
    <div className="mt-4 border-t border-border pt-4">
      <p className="text-sm font-medium text-foreground">{t('storage.eigen.zurueck.titel')}</p>
      <p className="mt-1 text-sm text-muted-foreground">
        {t(stand.returnApproved ? 'storage.eigen.zurueck.freigegeben' : 'storage.eigen.zurueck.gesperrt')}
      </p>
      {stand.returnApproved && (
        <button
          type="button"
          onClick={() => void zurueck()}
          disabled={beschaeftigt}
          className="mt-2 rounded-md border border-input bg-background px-3 py-1.5 text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50"
        >
          {t('storage.eigen.knopf.zurueck')}
        </button>
      )}
      {fehler && <p className="mt-2 text-sm text-destructive">{fehler}</p>}
    </div>
  )
}
